import React, { useEffect, useState } from 'react';
import { Input, Label, Button, Form, Spinner } from 'reactstrap';

import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import withRouter from '../../Components/Common/withRouter';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import {
  loginUser,
  finishLogin,
  idamanLogin,
  resetLoginFlag,
} from '../../slices/thunks';
import { apiError } from '../../slices/auth/login/reducer';
import logoSm from '../../assets/images/logo-sm.png';
import { createSelector } from 'reselect';

import AuthCardLayout from './AuthCardLayout';
import LoginErrorAlert from './LoginErrorAlert';
import LoginFieldError from './LoginFieldError';
import { isAuthenticated } from '../../auth/tokenvalidation';
import useAsyncAction from '../../Components/Hooks/useAsyncAction';
import LoginMfaStep from './Mfa/LoginMfaStep';
import RecoveryCodesPanel from './Mfa/RecoveryCodesPanel';
import { getLoginBlockedError } from './loginErrorUtils';
import {
  SESSION_END_CODES,
  SESSION_STARTED_KEY,
} from '../../helpers/sessionManager';

const Login = props => {
  const dispatch = useDispatch();
  const selectLayoutState = state => state;
  const loginpageData = createSelector(selectLayoutState, state => ({
    user: state.Account.user,
    error: state.Login.error,
    loading: state.Login.loading,
    errorMsg: state.Login.errorMsg,
  }));
  const { user, error, loading, errorMsg } = useSelector(loginpageData);
  const [userLogin, setUserLogin] = useState({});
  const [passwordShow, setPasswordShow] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Registered Supplier: challenge MFA dari BE setelah password benar.
  const [mfaChallenge, setMfaChallenge] = useState(null);
  // Hasil login-mfa yang membawa recovery code (enroll pertama): tampilkan dulu sebelum masuk.
  const [mfaLoginResult, setMfaLoginResult] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Force logout (idle / login di tempat lain / expired): sessionManager.forceLogout mengirim ?reason=<code>.
    const reason = new URLSearchParams(location.search).get('reason');
    // Gagal login IDAMAN: completeOidcLogin mengirim pesan lewat router state.
    // Tampilkan pesan itu (jangan di-reset), lalu hapus state agar tidak muncul lagi saat refresh.
    const oidcError = location.state?.oidcError;
    if (reason && SESSION_END_CODES.includes(reason)) {
      dispatch(apiError(`SESSION_END:${reason}`));
      // hapus ?reason agar pesan tidak muncul lagi saat refresh
      navigate(location.pathname, { replace: true, state: null });
    } else if (oidcError) {
      dispatch(apiError(oidcError));
      navigate(location.pathname, { replace: true, state: null });
    } else if (isAuthenticated()) {
      // Sesi dibagi antar tab: login kedua di browser yang sama akan mencabut sesi sendiri (single login).
      navigate('/dashboard', { replace: true });
    } else {
      dispatch(resetLoginFlag());
    }
    // Hanya saat mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    // Tab lain baru login: ikut masuk (reload penuh supaya state dibaca ulang dari localStorage).
    // Tanpa ini tab ini bisa menimpa persist:root bersama dengan state logged-out.
    const onStorage = e => {
      if (e.key === SESSION_STARTED_KEY && e.newValue) {
        window.location.replace('/dashboard');
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  useEffect(() => {
    if (user && user) {
      const updatedUserData =
        process.env.REACT_APP_DEFAULTAUTH === 'firebase'
          ? user.multiFactor.user.username
          : user.username;
      const updatedUserPassword =
        process.env.REACT_APP_DEFAULTAUTH === 'firebase'
          ? ''
          : user.confirm_password;
      setUserLogin({
        username: updatedUserData,
        password: updatedUserPassword,
      });
    }
  }, [user]);

  // Re-entry guard: a second Enter/click before the button re-renders as
  // disabled would otherwise dispatch a second login.
  const [submitLogin] = useAsyncAction(async values => {
    setIsSubmitting(true);
    try {
      const challenge = await dispatch(
        loginUser(values, props.router.navigate)
      );
      if (challenge?.mfaRequired) {
        setMfaChallenge(challenge);
      }
    } finally {
      setTimeout(() => {
        setIsSubmitting(false);
      }, 3000);
    }
  });

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      username: userLogin.username || '',
      password: userLogin.password || '',
    },
    validationSchema: Yup.object({
      username: Yup.string().required('Please Enter Your User Name'),
      password: Yup.string().required('Please Enter Your Password'),
    }),
    onSubmit: submitLogin,
  });

  useEffect(() => {
    if (!loading) {
      setIsSubmitting(false);
    }
  }, [loading]);

  const handleMfaSuccess = async data => {
    if (data?.recoveryCodes?.length) {
      setMfaLoginResult(data);
      return;
    }
    await dispatch(finishLogin(data, props.router.navigate));
  };

  // Tiket MFA kedaluwarsa, akun terkunci, atau sesi aktif di device lain: kembali ke form password.
  const handleMfaFatalError = err => {
    setMfaChallenge(null);
    dispatch(
      apiError(
        getLoginBlockedError(err) ||
          err?.response?.data?.message ||
          'Verification failed. Please sign in again.'
      )
    );
  };

  const cancelMfa = () => {
    setMfaChallenge(null);
    validation.setFieldValue('password', '');
  };

  const header = mfaLoginResult
    ? {
        title: 'Save your recovery codes',
        subtitle: 'Two-factor authentication is now active on your account.',
      }
    : mfaChallenge?.mfaSetupRequired
      ? {
          title: 'Set up two-factor authentication',
          subtitle:
            'Registered Supplier accounts must use an authenticator app to sign in.',
        }
      : mfaChallenge
        ? {
            title: 'Two-factor authentication',
            subtitle: 'Enter the code from your authenticator app.',
          }
        : {
            title: 'Welcome back',
            subtitle: 'Sign in to your account to continue',
          };

  document.title = 'HYROC | Digital Hydrocarbon Procurement';
  return (
    <AuthCardLayout title={header.title} subtitle={header.subtitle}>
      <LoginErrorAlert error={error} visible={errorMsg} />

      {mfaLoginResult ? (
        <RecoveryCodesPanel
          codes={mfaLoginResult.recoveryCodes}
          email={mfaLoginResult.email}
          continueLabel='Continue to Dashboard'
          onContinue={() =>
            dispatch(finishLogin(mfaLoginResult, props.router.navigate))
          }
        />
      ) : mfaChallenge ? (
        <LoginMfaStep
          challenge={mfaChallenge}
          onSuccess={handleMfaSuccess}
          onCancel={cancelMfa}
          onFatalError={handleMfaFatalError}
        />
      ) : (
        <Form
          className='flex-grow-1 d-flex flex-column'
          onSubmit={e => {
            e.preventDefault();
            validation.handleSubmit();
            // a11y: move focus to the first invalid field
            validation.validateForm().then(errs => {
              const firstInvalid = errs?.username
                ? 'username'
                : errs?.password
                  ? 'password-input'
                  : null;
              if (firstInvalid) {
                document.getElementById(firstInvalid)?.focus();
              }
            });
            return false;
          }}
          action='#'
        >
          <div className='mb-3'>
            <Label htmlFor='username' className='login-label'>
              User Name
            </Label>
            <div className='input-group login-input-group'>
              <span className='input-group-text'>
                <i className='ri-user-3-line' aria-hidden='true' />
              </span>
              <Input
                name='username'
                id='username'
                className='form-control'
                placeholder='Enter your username'
                type='text'
                autoComplete='username'
                aria-required='true'
                aria-invalid={
                  validation.touched.username && validation.errors.username
                    ? 'true'
                    : 'false'
                }
                aria-describedby={
                  validation.touched.username && validation.errors.username
                    ? 'username-error'
                    : undefined
                }
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.username || ''}
                invalid={
                  validation.touched.username && validation.errors.username
                    ? true
                    : false
                }
              />
            </div>
            <LoginFieldError
              id='username-error'
              message={
                validation.touched.username ? validation.errors.username : null
              }
            />
          </div>

          <div className='mb-3'>
            <Label className='login-label' htmlFor='password-input'>
              Password
            </Label>
            <div className='position-relative auth-pass-inputgroup'>
              <div className='input-group login-input-group'>
                <span className='input-group-text'>
                  <i className='ri-lock-2-line' aria-hidden='true' />
                </span>
                <Input
                  name='password'
                  id='password-input'
                  value={validation.values.password || ''}
                  type={passwordShow ? 'text' : 'password'}
                  className='form-control pe-5'
                  placeholder='Enter your password'
                  autoComplete='current-password'
                  aria-required='true'
                  aria-invalid={
                    validation.touched.password && validation.errors.password
                      ? 'true'
                      : 'false'
                  }
                  aria-describedby={
                    validation.touched.password && validation.errors.password
                      ? 'password-error'
                      : undefined
                  }
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  invalid={
                    validation.touched.password && validation.errors.password
                      ? true
                      : false
                  }
                />
              </div>
              <LoginFieldError
                id='password-error'
                message={
                  validation.touched.password
                    ? validation.errors.password
                    : null
                }
              />
              <button
                className='btn btn-link position-absolute end-0 top-50 translate-middle-y text-decoration-none material-shadow-none login-password-toggle'
                type='button'
                id='password-addon'
                onClick={() => setPasswordShow(!passwordShow)}
                aria-label='Show password'
                aria-pressed={passwordShow}
                aria-controls='password-input'
              >
                <i
                  className={`ri-${passwordShow ? 'eye-off' : 'eye'}-line align-middle`}
                  aria-hidden='true'
                />
              </button>
            </div>
          </div>

          <div className='d-flex align-items-center justify-content-between mb-4'>
            <div className='form-check mb-0'>
              <Input
                className='form-check-input'
                type='checkbox'
                value=''
                id='auth-remember-check'
              />
              <Label
                className='form-check-label login-check-label'
                htmlFor='auth-remember-check'
              >
                Remember me
              </Label>
            </div>
          </div>

          <Button
            disabled={isSubmitting || loading}
            className='btn login-btn-primary w-100 d-flex align-items-center justify-content-center'
            type='submit'
          >
            {isSubmitting ? (
              <>
                <Spinner size='sm' className='me-2' aria-hidden='true' />
                Logging in...
              </>
            ) : (
              <>
                <i className='ri-login-circle-line me-2' aria-hidden='true' />
                Sign In
              </>
            )}
          </Button>

          <div className='login-divider my-4'>
            <span>or continue with</span>
          </div>

          <Button
            type='button'
            className='btn login-btn-idaman w-100 d-flex align-items-center justify-content-center'
            onClick={e => {
              e.preventDefault();
              dispatch(idamanLogin());
            }}
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner size='sm' className='me-2' aria-hidden='true' />
                Processing...
              </>
            ) : (
              <>
                <img src={logoSm} alt='' height='22' className='me-2' />
                Login with idAMan
              </>
            )}
          </Button>

          <p className='login-register-note text-center mt-4 mb-0'>
            Don&apos;t have an account yet?{' '}
            <Link to='/register-supplier'>
              Register for Registered Supplier
            </Link>
          </p>
        </Form>
      )}
    </AuthCardLayout>
  );
};

export default withRouter(Login);
